import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { getShopSettings, updateShopSettings, uploadShopLogo, deleteShopLogo } from '@/api/shop';
import { deliveryCommissionDraft, isDeliveryCommission, parseDeliveryCommission } from '@/utils/deliveryCommission';

const FIELDS = ['name', 'address', 'street_address', 'street_address_2', 'city', 'state', 'zip_code', 'timezone'];
const ADDRESS = ['address', 'street_address', 'street_address_2', 'city', 'state', 'zip_code'];
const hasCommission = source => Object.prototype.hasOwnProperty.call(source || {}, 'deliveryCommission');
const sourceIdentity = () => JSON.stringify([window.lvbDesktop?.backendUrl || null, window.lvbDesktop?.localBackendUrl || null]);
const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  && ['cloud', 'offline'].includes(binding.source)
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
const draftOf = saved => ({
  ...Object.fromEntries(FIELDS.map(key => [key, typeof saved?.[key] === 'string' ? saved[key] : ''])),
  deliveryCommission: deliveryCommissionDraft(saved),
});
const validSettings = data => data && typeof data === 'object' && !Array.isArray(data)
  && typeof data.name === 'string' && typeof data.timezone === 'string' && typeof data.address === 'string'
  && (data.logo_id == null || typeof data.logo_id === 'string')
  && FIELDS.every(key => data[key] == null || typeof data[key] === 'string')
  && (!hasCommission(data) || isDeliveryCommission(data.deliveryCommission));

// Also bound non-cooperative adapters. Ending this wait never promises server cancellation.
async function boundedCall(call, config) {
  let timer; let abort;
  const stopped = new Promise((_, reject) => {
    abort = () => reject(new Error('Shop request stopped'));
    if (config.signal?.aborted) { abort(); return; }
    config.signal?.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => reject(new Error('Shop request timed out')), 15000);
  });
  try { return await Promise.race([call(), stopped]); }
  finally { clearTimeout(timer); config.signal?.removeEventListener('abort', abort); }
}

export function shopProfilePayload(draft, baseline) {
  const payload = draftOf(draft);
  delete payload.deliveryCommission;
  if (hasCommission(draft)) {
    const commission = parseDeliveryCommission(draft.deliveryCommission);
    const previous = hasCommission(baseline) ? baseline.deliveryCommission : null;
    if (commission !== previous) payload.deliveryCommission = commission;
  }
  ADDRESS.forEach(key => {
    payload[key] = payload[key].trim();
    if (payload[key] === (baseline?.[key] || '').trim()) delete payload[key];
  });
  if (typeof baseline?.location_baseline === 'string') payload.location_baseline = baseline.location_baseline;
  return payload;
}

// This mounted workflow owns drafts and credentials, not the lifetime of server writes.
export function useShopProfile({ canManage = false, refreshBranding } = {}) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [, render] = useState(0);
  // A replacement session must reopen the screen to acquire new authority.
  const cycle = useMemo(() => ({ principal: principal(sessionBinding), revision: sessionBinding?.revision,
    source: sessionBinding?.source, connection: sourceIdentity(), active: false, generation: 0, controller: null,
    settings: null, draft: {}, baseline: null, loading: true, loadError: '', editing: false,
    busy: false, operation: null, lastOperation: null, selectedFile: null, status: 'idle', message: '', locationMessage: '',
    uncertain: false, locationReceiptBaseline: null, commissionIntent: undefined,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, canManage, refreshBranding };
  const visible = !!cycle.principal && cycle.revision === revision
    && sessionBinding?.revision === cycle.revision && principal(sessionBinding) === cycle.principal
    && cycle.connection === sourceIdentity();
  const owns = () => cycle.active && !!cycle.principal && getActiveSessionRevision() === cycle.revision
    && latest.current.sessionBinding?.revision === cycle.revision
    && principal(latest.current.sessionBinding) === cycle.principal && cycle.connection === sourceIdentity();
  const online = () => owns() && cycle.source === 'cloud' && !isDesktopOffline();
  const writable = () => online() && latest.current.canManage && cycle.status !== 'retired';
  const publish = () => { if (owns()) render(value => value + 1); };
  const request = () => {
    const token = latest.current.getToken?.();
    return token && owns() ? { headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      suppressServerErrorNotification: true } : null;
  };
  const read = async config => {
    const result = await boundedCall(() => getShopSettings(config), config);
    if (!validSettings(result)) throw new Error('Invalid shop settings response');
    return result;
  };
  const load = async () => {
    if (!owns() || cycle.busy) return false;
    const generation = ++cycle.generation;
    cycle.controller?.abort();
    const controller = new AbortController(); cycle.controller = controller;
    const current = () => owns() && generation === cycle.generation;
    cycle.loading = true; cycle.loadError = ''; publish();
    try {
      const config = request();
      if (!config) throw new Error('Sign in again to load shop settings.');
      const saved = await read({ ...config, signal: controller.signal });
      if (!current()) return false;
      cycle.settings = saved; cycle.baseline = saved; cycle.draft = draftOf(saved);
      return true;
    } catch {
      if (current()) cycle.loadError = 'Shop settings could not be loaded. Retry loading.';
      return false;
    } finally {
      if (current()) { cycle.loading = false; cycle.controller = null; publish(); }
      else if (cycle.active && cycle.connection !== sourceIdentity()) render(value => value + 1);
    }
  };
  useLayoutEffect(() => {
    cycle.active = true; load();
    return () => { cycle.active = false; cycle.generation += 1; cycle.controller?.abort(); };
  // The mounted workflow is deliberately not rebound by callback identities.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle]);
  useLayoutEffect(() => {
    if (!visible || !canManage) {
      if (!visible) cycle.controller?.abort();
      if (cycle.busy) {
        cycle.generation += 1; cycle.controller?.abort(); cycle.status = 'retired';
        cycle.busy = false; cycle.operation = null;
        cycle.message = 'This operation was retired after access changed. Reopen Shop Profile to check the saved settings.';
        publish();
      }
      cycle.editing = false;
    }
  // Permission transitions retire work; callback identity is not a transition.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, visible, canManage]);

  const refreshAfter = async (config, current, kind) => {
    let saved;
    try {
      saved = await read(config);
      if (!current()) return false;
      cycle.settings = saved;
      if (kind === 'save' && cycle.commissionIntent !== undefined
        && (hasCommission(saved) ? saved.deliveryCommission : null) !== cycle.commissionIntent) {
        cycle.status = 'unknown'; cycle.uncertain = true;
        cycle.message = 'The saved driver cost does not match your change. Your draft is retained. Check saved settings before retrying.';
        return false;
      }
    } catch {
      if (current()) {
        cycle.status = 'refresh-required';
        cycle.message = kind === 'save' && cycle.commissionIntent !== undefined
          ? 'The request was acknowledged, but the saved driver cost could not be confirmed. Retry refresh only; do not repeat the write.'
          : 'The change was saved, but saved settings could not refresh. Retry refresh only; do not repeat the write.';
      }
      return false;
    }
    let refreshed = false;
    try {
      if (latest.current.refreshBranding) refreshed = await latest.current.refreshBranding({ isCurrent: current });
    } catch { refreshed = false; }
    if (!current()) return false;
    if (!refreshed) {
      cycle.status = 'refresh-required';
      cycle.message = 'The change was saved, but shared shop information could not refresh. Retry refresh only.';
      return false;
    }
    if (kind === 'save') { cycle.draft = draftOf(saved); cycle.baseline = saved; cycle.editing = false; }
    if (kind === 'location' && saved.location_baseline === cycle.locationReceiptBaseline
      && (!cycle.editing || ADDRESS.every(key => (cycle.baseline?.[key] || '') === (saved[key] || '')))) {
      // A successful same-address check changes its token, without replacing the editor's draft.
      cycle.baseline = saved;
    }
    cycle.status = 'idle'; cycle.selectedFile = null; cycle.lastOperation = null;
    cycle.message = kind === 'save' ? `Shop settings saved.${cycle.locationMessage ? ` ${cycle.locationMessage}` : ''}`
      : kind === 'location' ? cycle.locationMessage || 'Shop map location updated.'
      : kind === 'upload' ? 'Logo uploaded.' : 'Logo removed.';
    return true;
  };
  const begin = operation => {
    if (!writable() || cycle.busy || !cycle.settings) return null;
    const config = request();
    if (!config) return null;
    const generation = ++cycle.generation;
    const controller = new AbortController(); cycle.controller = controller;
    cycle.busy = true; cycle.operation = operation; publish();
    return { generation, config: { ...config, signal: controller.signal },
      current: () => writable() && generation === cycle.generation };
  };
  const finish = ({ current, generation }) => {
    if (!current()) {
      if (cycle.active && generation === cycle.generation) {
        cycle.busy = false; cycle.operation = null; cycle.controller = null;
        cycle.status = 'retired';
        cycle.message = 'The connection or access changed while this operation was pending. Reopen Shop Profile to check saved settings; the earlier request may still finish.';
        render(value => value + 1);
      }
      return;
    }
    cycle.busy = false; cycle.operation = null; cycle.controller = null; publish();
  };
  const mutate = async (kind, file, retry = false) => {
    if (!writable() || cycle.busy || !(retry ? ['rejected', 'checked'] : ['idle']).includes(cycle.status)) return false;
    if (kind === 'save' && !cycle.editing) return false;
    if (kind === 'location' && (!cycle.settings?.route_origin?.address || !cycle.settings.location_baseline)) return false;
    if (kind === 'upload' && (!file || file.size > 5 * 1024 * 1024
      || !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type))) {
      cycle.message = 'Choose a PNG, JPEG, WebP or SVG logo, 5 MB or smaller.'; publish(); return false;
    }
    let savePayload;
    try { savePayload = kind === 'save' ? shopProfilePayload(cycle.draft, cycle.baseline) : null; }
    catch (error) { cycle.message = error.message; publish(); return false; }
    if (savePayload && retry && cycle.status === 'checked' && typeof cycle.settings.location_baseline === 'string') {
      // Only a deliberate retry after readback may submit the retained edit against that observed revision.
      savePayload.location_baseline = cycle.settings.location_baseline;
    }
    const operation = begin(kind); if (!operation) return false;
    const { config, current } = operation;
    cycle.lastOperation = kind; cycle.selectedFile = kind === 'upload' ? file : null;
    cycle.commissionIntent = kind === 'save' && hasCommission(savePayload)
      ? savePayload.deliveryCommission : undefined;
    cycle.locationMessage = ''; cycle.locationReceiptBaseline = null;
    cycle.message = kind === 'save' ? 'Saving shop settings...' : kind === 'location' ? 'Updating shop map location...'
      : kind === 'upload' ? 'Uploading logo...' : 'Removing logo...';
    try {
      try {
        const ack = await boundedCall(() => kind === 'save'
          ? updateShopSettings(savePayload, config)
          : kind === 'location' ? updateShopSettings({ location_action: 'refresh', location_baseline: cycle.settings.location_baseline }, config)
          : kind === 'upload' ? uploadShopLogo(file, config) : deleteShopLogo(config), config);
        if (!current()) return false;
        if (ack?.success === false) {
          const error = new Error('The change was rejected');
          error.response = { status: 400, data: { detail: ack.detail } };
          throw error;
        }
        if (ack?.success !== true || (kind === 'upload' && (typeof ack.logo_id !== 'string' || !ack.logo_id))) {
          throw new Error('Unconfirmed acknowledgement');
        }
        // Only the logo receipt is a complete field update; profile receipt is partial.
        if (kind === 'upload') cycle.settings = { ...cycle.settings, logo_id: ack.logo_id };
        if (kind === 'delete') cycle.settings = { ...cycle.settings, logo_id: null };
        if (['save', 'location'].includes(kind)) {
          cycle.locationReceiptBaseline = ack.location_baseline || null;
          cycle.locationMessage = typeof ack.detail === 'string' ? ack.detail
            : ack.location_status === 'check_failed' ? 'The location check failed. The previously established shop point remains in use.'
            : ack.location_status === 'unavailable' ? 'The address is saved, but its map location is unavailable. An administrator can update the shop map location.'
            : kind === 'location' ? 'Shop map location updated.' : '';
        }
      } catch (error) {
        if (!current()) return false;
        const status = error?.response?.status;
        const rejected = status >= 400 && status < 500 && status !== 408;
        cycle.status = rejected ? 'rejected' : 'unknown';
        cycle.uncertain = !rejected;
        cycle.message = rejected
          ? `${typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'The change was rejected.'} Your draft or file is retained; retry when ready.`
          : 'The result is unconfirmed; the change may have succeeded. Your draft or file is retained. Check saved settings before retrying.';
        return false;
      }
      return await refreshAfter(config, current, kind);
    } finally { finish(operation); }
  };
  const checkSavedSettings = async () => {
    if (!['unknown', 'checked', 'rejected'].includes(cycle.status)) return false;
    const operation = begin('check'); if (!operation) return false;
    const { config, current } = operation;
    try {
      const saved = await read(config);
      if (!current()) return false;
      cycle.settings = saved;
      cycle.status = 'checked';
      cycle.message = cycle.uncertain
        ? 'Current saved settings are shown; your draft or file is retained. The earlier request may still finish. Accept the current state or deliberately retry only if you want to submit again.'
        : 'Current saved settings are shown; the rejected change was not applied. Your draft or file is retained. Accept the current state or deliberately retry your change.';
      // A recovered snapshot may refresh public consumers, but cannot assert write causality.
      try { await latest.current.refreshBranding?.({ isCurrent: current }); } catch { /* Snapshot stays a snapshot. */ }
      return current();
    } catch {
      if (current()) { cycle.status = cycle.uncertain ? 'unknown' : 'rejected'; cycle.message = 'Saved settings could not be checked. Check again before retrying the write.'; }
      return false;
    } finally { finish(operation); }
  };
  const retryRefresh = async () => {
    if (cycle.status !== 'refresh-required') return false;
    const operation = begin('refresh'); if (!operation) return false;
    try { return await refreshAfter(operation.config, operation.current, cycle.lastOperation); }
    finally { finish(operation); }
  };
  const setDraft = value => {
    if (!writable() || cycle.busy || !cycle.editing || !['idle', 'rejected'].includes(cycle.status)) return;
    cycle.draft = draftOf(typeof value === 'function' ? value(cycle.draft) : value); publish();
  };
  const acceptSavedSettings = () => {
    if (!writable() || cycle.busy || cycle.status !== 'checked') return;
    if (cycle.lastOperation === 'save') {
      cycle.draft = draftOf(cycle.settings); cycle.baseline = cycle.settings; cycle.editing = false;
    }
    if (cycle.lastOperation === 'location' && (!cycle.editing || ADDRESS.every(key => (cycle.baseline?.[key] || '') === (cycle.settings?.[key] || '')))) {
      cycle.baseline = cycle.settings;
    }
    cycle.status = 'idle'; cycle.message = cycle.uncertain
      ? 'Current saved settings accepted. An earlier request may still finish.' : 'Current saved settings accepted.';
    cycle.lastOperation = null; cycle.selectedFile = null; publish();
  };
  const discardSelection = () => {
    if (!writable() || cycle.busy || cycle.lastOperation !== 'upload' || !['rejected', 'checked'].includes(cycle.status)) return;
    cycle.selectedFile = null; cycle.lastOperation = null; cycle.status = 'idle';
    cycle.message = 'Selected file discarded.'; publish();
  };
  return {
    settings: visible ? cycle.settings : null, draft: visible ? cycle.draft : {}, setDraft,
    deliveryCommissionAvailable: visible && online(),
    editing: visible && canManage && cycle.editing,
    startEditing: () => {
      if (!writable() || cycle.busy || !cycle.settings || cycle.status !== 'idle') return;
      cycle.draft = draftOf(cycle.settings); cycle.baseline = cycle.settings; cycle.editing = true; cycle.message = ''; publish();
    },
    cancelEditing: () => {
      if (!writable() || cycle.busy || !['idle', 'rejected'].includes(cycle.status)) return;
      cycle.draft = draftOf(cycle.settings); cycle.baseline = cycle.settings; cycle.editing = false;
      if (cycle.lastOperation === 'save') { cycle.lastOperation = null; cycle.status = 'idle'; cycle.message = ''; }
      publish();
    },
    loading: visible && cycle.loading, loadError: visible ? cycle.loadError : 'The connection or sign-in session changed. Reopen Shop Profile.',
    retryLoading: () => !cycle.settings && load(), busy: visible && cycle.busy,
    operation: visible ? cycle.operation : null, status: visible ? cycle.status : 'retired',
    uncertain: cycle.uncertain,
    message: visible ? cycle.message : '', selectedFile: visible ? cycle.selectedFile : null,
    readOnly: !visible || !writable() || cycle.busy,
    draftReadOnly: !visible || !writable() || cycle.busy || !['idle', 'rejected'].includes(cycle.status),
    canMutate: visible && writable() && !cycle.busy && !!cycle.settings && cycle.status === 'idle',
    save: () => mutate('save'), uploadLogo: file => mutate('upload', file), removeLogo: () => mutate('delete'),
    updateMapLocation: () => mutate('location'),
    retryMutation: () => mutate(cycle.lastOperation, cycle.selectedFile, true),
    checkSavedSettings, acceptSavedSettings, retryRefresh, discardSelection,
  };
}
